import { useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Button, cx } from '@timely/ui';
import { holds, type Capability } from '@timely/domain';
import { useSession } from '../auth/SessionProvider';
import { useActiveWorkspace, useWorkspaces } from './WorkspaceProvider';
import { shouldShowSwitcher } from './resolve';
import { CompanyMark } from '../branding/CompanyMark';
import { titled, useDocumentTitle } from '../lib/documentTitle';

/**
 * The chrome around every screen inside a company.
 *
 * The navigation is built from capabilities, not from role names. A person who
 * cannot manage roles has no Roles tab; a person who cannot read the audit log
 * has no Audit tab. That is a courtesy and nothing more — the policies and the
 * server routes refuse the same person if they type the address — but it is the
 * difference between a product that fits the person using it and one that
 * offers them six things and refuses five.
 */

interface Destination {
    to: string;
    label: string;
    /** Absent means everybody in the company sees it. */
    needs?: Capability;
    /** Shown to anybody holding at least one of these. */
    needsAny?: readonly Capability[];
}

const DESTINATIONS: Destination[] = [
    { to: 'today', label: 'Today' },
    { to: 'book', label: 'Book' },
    { to: 'cheques', label: 'Cheques', needs: 'cheques.view' },
    { to: 'reports', label: 'Reports', needs: 'reports.view.own' },
    { to: 'stock', label: 'Live stock', needs: 'stock.view' },
    { to: 'data', label: 'Data source', needs: 'datasource.view' },
    { to: 'templates', label: 'Templates', needs: 'templates.manage' },
    { to: 'alerts', label: 'Alerts', needs: 'integrations.manage' },
    { to: 'ai', label: 'AI', needs: 'integrations.manage' },
    { to: 'team', label: 'Team', needs: 'team.view' },
    { to: 'roles', label: 'Roles', needs: 'roles.manage' },
    { to: 'settings', label: 'Settings', needs: 'settings.manage' },
    { to: 'audit', label: 'Audit', needs: 'workspace.audit.view' },
    { to: 'company', label: 'Company data', needsAny: ['workspace.export', 'workspace.reset', 'workspace.delete'] },
];

/** Screens reached from inside another one, named for the page title. */
const OTHER_SCREENS: Record<string, string> = {
    setup: 'Company setup',
    reconciliation: 'Ownership codes',
};

/** What the screen at this address is called: the tab it hangs off, or its own name. */
export function screenName(pathname: string, slug: string): string {
    const rest = pathname.startsWith(`/w/${slug}`) ? pathname.slice(`/w/${slug}`.length).replace(/^\//, '') : '';
    const [first = '', second] = rest.split('/');
    if (first === 'book' && second) return 'Account';
    return DESTINATIONS.find(d => d.to === (first || 'today'))?.label ?? OTHER_SCREENS[first] ?? 'Timely Payment';
}

export function WorkspaceShell() {
    const context = useActiveWorkspace();
    const { contexts } = useWorkspaces();
    const { email, signOut } = useSession();
    const { workspace } = context;
    const { pathname } = useLocation();
    const nav = useRef<HTMLElement>(null);
    const heading = useRef<HTMLHeadingElement>(null);
    const lastPath = useRef<string | null>(null);

    const visible = DESTINATIONS.filter(d =>
        (!d.needs || holds(context, d.needs)) && (!d.needsAny || d.needsAny.some(c => holds(context, c))));

    const screen = screenName(pathname, workspace.slug);
    useDocumentTitle(titled(screen, workspace.name));

    useEffect(() => {
        // On a phone the sections scroll sideways; the one you are in must be
        // on screen, or nothing says where you are.
        nav.current?.querySelector<HTMLElement>('[aria-current="page"]')
            ?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
        // Moving between screens in place announces nothing to a screen
        // reader. Focus goes to the new page's heading — only when the path
        // really changed, not on the first load (where the browser's own focus
        // is already right) and not when an effect merely runs twice.
        if (lastPath.current !== null && lastPath.current !== pathname) heading.current?.focus();
        lastPath.current = pathname;
    }, [pathname]);

    return (
        <div className="min-h-dvh bg-bg">
            <a
                href="#main"
                className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2.5 focus:rounded-[10px] focus:bg-card focus:text-label focus:shadow-card"
            >
                Skip to content
            </a>
            <header className="border-b border-separator bg-card">
                <div className="max-w-5xl mx-auto px-5 py-3.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <span
                            className="w-8 h-8 rounded-[9px] bg-accent text-on-accent grid place-items-center font-bold text-[12px] flex-none"
                            aria-hidden="true"
                        >
                            TP
                        </span>
                        <div className="min-w-0">
                            {/* The product first, the company beneath it. A customer's
                                name never replaces Timely Payment's. */}
                            <div className="text-[14px] font-bold text-label leading-tight truncate">
                                Timely Payment
                            </div>
                            <div className="text-[12.5px] text-label-3 leading-tight min-w-0 flex items-center gap-1.5">
                                {/* Secondary to the Timely Payment mark above — the
                                    company's own, shown small, initials when there is
                                    no logo rather than a blank space. */}
                                <CompanyMark name={workspace.name} logoPath={workspace.logoPath ?? null} />
                                <span className="truncate min-w-0">{workspace.name}</span>
                                {/* No switcher at all when there is only one company
                                    to switch to (owner decision D2). */}
                                {shouldShowSwitcher(contexts) && (
                                    <>
                                        <span aria-hidden="true" className="flex-none">·</span>
                                        {/* Outside the truncated name, so the padding
                                            — the finger's, not the eye's — is not clipped;
                                            the negative margin keeps the line where it was. */}
                                        <a href="/" className="flex-none text-accent hover:underline px-1.5 py-2 -mx-1.5 -my-2" aria-label="Switch company">Switch</a>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 flex-none">
                        <span className="text-[12.5px] text-label-3 max-md:hidden">{email}</span>
                        <Button size="sm" variant="quiet" onClick={() => { void signOut(); }}>
                            Sign out
                        </Button>
                    </div>
                </div>

                {visible.length > 1 && (
                    <nav
                        ref={nav}
                        aria-label="Sections"
                        // On narrow screens the row scrolls; the fade at the right
                        // edge says there is more of it, and the padding keeps the
                        // last section clear of the fade once scrolled to.
                        className="max-w-5xl mx-auto px-5 flex items-center gap-1 overflow-x-auto max-md:pr-10 max-md:[mask-image:linear-gradient(to_right,#000_calc(100%-2.5rem),transparent)]"
                    >
                        {visible.map(d => (
                            <NavLink
                                key={d.to}
                                to={`/w/${workspace.slug}/${d.to}`}
                                className={({ isActive }) => cx(
                                    'px-3 py-2.5 text-[13.5px] font-semibold whitespace-nowrap',
                                    'border-b-2 -mb-px transition-colors',
                                    isActive
                                        ? 'border-accent text-accent'
                                        : 'border-transparent text-label-3 hover:text-label',
                                )}
                            >
                                {d.label}
                            </NavLink>
                        ))}
                    </nav>
                )}
            </header>

            {/* The page's one first-level heading. The screens' own titles are
                second-level, under it; this names the page for a screen reader
                and is where focus lands after moving between screens. */}
            <h1 ref={heading} tabIndex={-1} className="sr-only">{screen}, {workspace.name}</h1>

            <Outlet />
        </div>
    );
}

/** The page frame every screen inside the shell uses, so widths agree. */
export const Page = ({ children }: { children: React.ReactNode }) => (
    <main id="main" tabIndex={-1} className="max-w-5xl mx-auto px-5 py-7 focus:outline-none">{children}</main>
);
